import React, { useContext, useState, useEffect } from 'react';
import { Box, Typography, IconButton, Menu, MenuItem, Badge, Chip, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Button, Link } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import StarsIcon from '@mui/icons-material/Stars';
import BackpackIcon from '@mui/icons-material/Backpack';
import { getAuth } from 'firebase/auth';
import { AppContext } from '../AppContext';
import pointsService from '../services/pointsService';

function DynamicIslandHeader({ noteCount = 0, onNotificationClick, onPocketClick, refreshTrigger = 0 }) {
  const auth = getAuth();
  const { user } = useContext(AppContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [userPoints, setUserPoints] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const open = Boolean(anchorEl);

  // Fetch user points
  useEffect(() => {
    async function fetchUserPoints() {
      if (auth.currentUser) {
        const points = await pointsService.getUserPoints(auth.currentUser.uid);
        setUserPoints(points);
      }
    }
    fetchUserPoints();

    // Refresh points every 30 seconds
    const interval = setInterval(fetchUserPoints, 30000);
    return () => clearInterval(interval);
  }, [auth.currentUser, refreshTrigger]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    auth.signOut();
    setAnchorEl(null);
  };

  const handleSettings = () => {
    setAnchorEl(null);
    setSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Dynamic Island Container */}
      <Box
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          borderRadius: '24px',
          padding: '8px 16px 8px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        }}
      >
        {/* Brand name - only visible on larger screens */}
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Gochi Hand, cursive',
            color: 'white',
            fontWeight: 600,
            fontSize: '1.2rem',
            display: { xs: 'none', sm: 'none', md: 'block' },
          }}
        >
          dropped.ink
        </Typography>

        {/* Divider - only visible on larger screens */}
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            height: '24px',
            alignSelf: 'center',
            display: { xs: 'none', sm: 'none', md: 'block' },
          }}
        />

        {noteCount > 0 && (
          <IconButton
            onClick={onNotificationClick}
            sx={{
              color: 'white',
              padding: '8px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <Badge
              badgeContent={noteCount}
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: '#ff3b30',
                  color: 'white',
                  fontSize: '0.7rem',
                  height: '18px',
                  minWidth: '18px',
                  fontWeight: 600,
                },
              }}
            >
              <StickyNote2Icon sx={{ fontSize: 20 }} />
            </Badge>
          </IconButton>
        )}

        <IconButton
          onClick={onPocketClick}
          sx={{
            color: 'white',
            padding: '8px',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
          aria-label="open pocket"
        >
          <BackpackIcon sx={{ fontSize: 20 }} />
        </IconButton>

        {/* Combined User Profile + Points Pill */}
        <Box
          onClick={handleMenu}
          aria-label="account menu"
          aria-controls="account-menu"
          aria-haspopup="true"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '20px',
            padding: '4px 12px 4px 4px',
            cursor: 'pointer',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            },
          }}
        >
          <AccountCircleIcon sx={{ fontSize: 28, color: 'white' }} />
          {userPoints && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <StarsIcon sx={{ fontSize: 16, color: '#FFD700' }} />
              <Typography
                variant="body2"
                sx={{
                  color: '#FFD700',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              >
                {userPoints.currentPoints}
              </Typography>
            </Box>
          )}
        </Box>

        <Menu
          id="account-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              mt: 1,
              minWidth: 200,
              borderRadius: '12px',
            },
          }}
        >
          {userPoints && (
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 600 }}>
                Level {userPoints.level}: {userPoints.levelName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {userPoints.currentPoints} points • {userPoints.notesCollected} collected
              </Typography>
            </Box>
          )}
          <MenuItem
            onClick={handleSettings}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Settings
          </MenuItem>
          <MenuItem
            onClick={handleLogout}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Logout
          </MenuItem>
        </Menu>
      </Box>

      {/* Settings Dialog */}
      <Dialog
        open={settingsOpen}
        onClose={handleSettingsClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Gochi Hand, cursive', fontSize: '1.5rem' }}>
          Settings
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontFamily: 'Gochi Hand, cursive' }}>
              Credits
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                <Link href="https://www.flaticon.com/free-icons/bronze" target="_blank" rel="noopener" sx={{ color: '#2196F3' }}>
                  Bronze icons created by littleicon - Flaticon
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                <Link href="https://www.flaticon.com/free-icons/game-coin" target="_blank" rel="noopener" sx={{ color: '#2196F3' }}>
                  Game coin icons created by littleicon - Flaticon
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                <Link href="https://www.flaticon.com/free-icons/gold" target="_blank" rel="noopener" sx={{ color: '#2196F3' }}>
                  Gold icons created by littleicon - Flaticon
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                <Link href="https://www.flaticon.com/free-icons/platinum" target="_blank" rel="noopener" sx={{ color: '#2196F3' }}>
                  Platinum icons created by littleicon - Flaticon
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                <Link href="https://www.flaticon.com/free-icons/nft" target="_blank" rel="noopener" sx={{ color: '#2196F3' }}>
                  Nft icons created by Freepik - Flaticon
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                <Link href="https://www.flaticon.com/free-icons/paper" target="_blank" rel="noopener" sx={{ color: '#2196F3' }}>
                  Paper icons created by Freepik - Flaticon
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                <Link href="https://www.flaticon.com/free-icons/coin" target="_blank" rel="noopener" sx={{ color: '#2196F3' }}>
                  Coin icons created by Kason Koo - Flaticon
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                <Link href="https://www.flaticon.com/free-icons/game-badge" target="_blank" rel="noopener" sx={{ color: '#2196F3' }}>
                  Game badge icons created by Kason Koo - Flaticon
                </Link>
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleSettingsClose}
            variant="contained"
            sx={{
              fontFamily: 'Gochi Hand, cursive',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DynamicIslandHeader;
